import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCommentContract } from '@modules/build-hub-comment/hooks/useCommentContract';
import type { Candidate } from '@modules/build-hub-comment/components/CommentAutoCompleter';
import { CommentAutoComplete } from '@modules/build-hub-comment/components/CommentAutoCompleter';
import { CommentSubmitPayload as Schema } from '@modules/build-hub-comment/types';
import type { CommentSubmitPayload, Entity } from '@modules/build-hub-comment/types';
import { Button } from '@rocco/components/button';

interface FormValues {
    documentId?: string;
    message: string;
}

export function CommentForm({
    documentId,
    onSubmit,
    candidates,
}: {
    documentId?: string;
    onSubmit: (payload: CommentSubmitPayload) => Promise<void> | void;
    candidates: Candidate[];
}) {
    const { register, handleSubmit, reset } = useForm<FormValues>({
        resolver: zodResolver(Schema.pick({ message: true, context: true })),
        defaultValues: { documentId, message: "" },
    });

    const { encode } = useCommentContract();

    const [query, setQuery] = React.useState<string | null>(null);
    const [entities, setEntities] = React.useState<Entity[]>([]);
    const [highlightIndex, setHighlightIndex] = React.useState<number>(0);
    const [triggerStart, setTriggerStart] = React.useState<number | null>(null);

    const filteredCandidates = query
        ? candidates.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
        : candidates;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (query !== null) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightIndex((i) => (i + 1) % filteredCandidates.length);
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => (i - 1 + filteredCandidates.length) % filteredCandidates.length);
                return;
            }
            if (e.key === "Enter") {
                e.preventDefault();
                const choice = filteredCandidates[highlightIndex];
                if (choice) handleSelect(choice);
                return;
            }
            if (e.key === " ") {
                setQuery(null);
                setTriggerStart(null);
            }
        } else {
            if (e.key === "@") {
                setTriggerStart(e.currentTarget.selectionStart); // mark '@' index
                setQuery("");
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        const cursor = e.target.selectionStart;

        if (triggerStart !== null) {
            if (cursor <= triggerStart) {
                // caret went back before '@', cancel mention
                setTriggerStart(null);
                setQuery(null);
                return;
            }
            const q = val.slice(triggerStart + 1, cursor);
            setQuery(q);
            setHighlightIndex(0);
        }
    };

    const handleSelect = (c: Candidate) => {
        const textarea = document.getElementById("comment-msg") as HTMLTextAreaElement;
        if (!textarea || triggerStart === null) return;

        const liveValue = textarea.value; // 👈 always read the live message
        const caret = textarea.selectionStart;

        // replace substring from triggerStart .. caret with "@Label"
        const before = liveValue.slice(0, triggerStart);
        const after = liveValue.slice(caret);
        const insertion = `@${c.label}`;
        const newMessage = before + insertion + after;

        setEntities((prev) => [...prev, { type: c.type, id: c.id, label: c.label } as Entity]);

        // reset form with new message
        reset({ documentId, message: newMessage });
        setQuery(null);
        setTriggerStart(null);

        // move caret after inserted text
        requestAnimationFrame(() => {
            textarea.selectionStart = textarea.selectionEnd = before.length + insertion.length;
            textarea.focus();
        });
    };

    const submitNow = handleSubmit(async (values) => {
        const payload = encode(values.message, entities, { documentId });
        await onSubmit(payload);
        reset({ documentId, message: "" });
        setEntities([]);
        setTriggerStart(null);
        setQuery(null);
    });

    return (
        <div className="relative rounded-xl border grid gap-y-4">
            <textarea
                id="comment-msg"
                {...register("message")}
                className="min-h-[96px] rounded-lg border p-2 outline-none"
                placeholder="Write a comment… use @name to mention"
                onKeyDown={handleKeyDown}
                onChange={handleChange}
            />
            {query !== null && (
                <CommentAutoComplete
                    query={query}
                    candidates={filteredCandidates}
                    highlightIndex={highlightIndex}
                    onSelect={handleSelect}
                    onCancel={() => {
                        setQuery(null);
                        setTriggerStart(null);
                    }}
                />
            )}
            <div className="flex justify-end gap-2">
                <Button
                    color="dark"
                    textSize={'small'}
                    className="leading-[1.5rem]"
                    onClick={() =>
                        submitNow()
                    }
                >
                    Post
                </Button>
            </div>
        </div>
    );
}
